/**
 * Exports as the person does them in the classic interface: the options
 * dialog, progress cards, the download and its toast, the warnings dialog.
 * The engine builds what is exported (engine/crypto/data-export.js,
 * engine/services/map-export.js, shared/utils/map-archive.js); moved here from those
 * modules in Phase 5b, step 2, unchanged.
 */
import { chooseExportOptions } from './export-options.js';
import { createModal, showModal, closeModal } from './modals.js';
import { showToast } from './toast.js';
import { withOperation } from './operation-progress.js';
import { buildMapArchive } from '../../../shared/utils/map-archive.js';
import { saveFileFromBlob } from './save-file.js';
import { VaultStorage } from '../../../engine/crypto/vault-storage.js';
import { archiveDocuments, buildLocalCopyExport } from '../../../engine/crypto/data-export.js';
import { getExportAttachment } from '../../../engine/services/map-export.js';
import { listAttachments } from '../../../engine/services/attachment-service.js';
import { escapeHtml } from '../../../engine/core/validation.js';
import { t } from '../../../shared/i18n/index.js';

/** The caller captures data/docId before opening the chooser. */
export async function saveMapExport(blob, filename, { includeAttachments, zip = false, data = null,
  docId = null, documents = null, allAttachments = true, warnings = [] } = {}) {
  if (includeAttachments === undefined) {
    const choice = await chooseExportOptions();
    if (!choice) return null;
    includeAttachments = choice.includeAttachments;
  }
  return withOperation(t('vault.export.mapTitle'), async progress => {
    if (!includeAttachments && !zip) {
      progress.update({ label: t('vault.export.preparingDownload') });
      await saveFileFromBlob(blob, filename, { onProgress: progress.update, throwOnError: true });
      return { warnings, attachmentsIncluded: false };
    }
    const scopedDocs = documents || [{ id: docId || 'exported-map', data,
      listAll: !!docId && allAttachments && VaultStorage.getDoc(docId)?._storage !== 'local' }];
    const result = await buildMapArchive({ blob, filename, documents: scopedDocs,
      includeAttachments, warnings, list: id => listAttachments(id),
      download: getExportAttachment,
      onProgress: (current, total) => progress.update({ label: t('vault.export.collectingAttachments'), current, total }),
      onActivity: progress.update,
    });
    await saveFileFromBlob(result.blob, filename.replace(/\.[^.]+$/, '') + '.zip', { onProgress: progress.update, throwOnError: true });
    if (result.warnings.length) showExportWarnings(result.warnings);
    showToast(t(result.warnings.length ? 'vault.export.downloadedWithWarnings' : 'vault.export.downloaded'),
      { type: result.warnings.length ? 'warning' : 'success' });
    return result;
  }, result => [t(result.warnings.length ? 'vault.export.readyWithWarnings' : 'vault.export.ready'), result.warnings.length > 0]);
}

/**
 * Download export data as a JSON file
 * @param {Object} exportData - Export data object
 * @param {string} filename - Filename (default: orbital-export-{date}.json)
 */
export async function downloadExport(exportData, filename = null, options = {}) {
  if (options.includeAttachments === undefined) {
    const choice = await chooseExportOptions();
    if (!choice) return null;
    options = { ...options, ...choice };
  }
  const date = new Date().toISOString().split("T")[0];
  const defaultFilename = `orbital-export-${date}.json`;

  const exported = options.includeAttachments ? { ...exportData, backup: { ...exportData.backup,
    attachmentsIncluded: true, attachmentInventory: 'orbital-manifest.json' } } : exportData;
  const json = JSON.stringify(exported, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  return saveMapExport(blob, filename || defaultFilename, { ...options,
    warnings: exportData.backup?.warnings || [],
    documents: archiveDocuments(exportData, options.includeAttachments),
  });
}

/**
 * Save a map's copy kept on this device as a file (engine/crypto/data-export.js
 * buildLocalCopyExport): before forgetting a map whose online copy is gone.
 * @returns {Promise<boolean>} whether a file was saved
 */
export async function exportLocalCopy(docId, title) {
  const { exportData, filename } = await buildLocalCopyExport(docId, title);
  return !!(await downloadExport(exportData, filename, { includeAttachments: false }));
}

/**
 * Show a modal with export warnings for shared maps that could not be exported
 * @param {Array<{name: string, error: string}>} errors - List of export errors
 */
export function showExportWarnings(errors) {
  const id = `export-warnings-${Date.now()}`;
  const listHtml = errors.map(e => typeof e === 'string' ? { name: t('common.export'), error: e } : e).map(e =>
    `<div class="export-warning-item">
      <span class="export-warning-name">${escapeHtml(e.name)}</span>
      <span class="export-warning-error">${escapeHtml(e.error)}</span>
    </div>`
  ).join("");

  const modal = createModal({
    id,
    title: t('vault.warnings.title'),
    className: "export-warnings-modal",
    content: `
      <p style="margin-bottom: 12px; color: var(--text-secondary);">
        ${escapeHtml(t('vault.warnings.intro'))}
      </p>
      <div class="export-warnings-list" style="max-height: 300px; overflow-y: auto;">
        ${listHtml}
      </div>
    `,
    buttons: [
      { text: t('common.ok'), action: "ok", className: "modal-btn-primary" },
    ],
  });

  modal.querySelector('[data-action="ok"]').addEventListener("click", () => {
    closeModal(id);
    modal.remove();
  });

  document.body.appendChild(modal);
  showModal(id);
}
